import router from "../../router/index.js";

/**
 * State
 */
const state = {
    reference: null,
    booking: {
        address: {
            postcode: null
        },
        availability: {},
        billing_first_name: null,
        billing_last_name: null,
        billing_address: {},
        braintree_client_token: null,
        cart: {
            devices: {},
            discount_code: null,
            discount_code_amount: 0,
            discount_percentage: 0,
            full_price: 0,
            sale_active: false,
            sale_title: null,
            total_price: 0
        },
        checkout_status: null,
        date: null,
        email: null,
        email_consent: false,
        first_name: null,
        full_date: null,
        in_warranty: null,
        klarna_client_token: null,
        klarna_session_id: null,
        last_name: null,
        phone_number: null,
        purchase_date: null,
        restricted_problems: [],
        drop_off_company_store_id: null
    },
    repairLocationOption: 'doorstep',
    useManualAddress: false,
    landingPageLoaded: false,
    landingPageLoadSuccess: true,
    disableBasketButtonOnConfirmDamagePage: true,
    requestedDropOffStoreId: null,
    customerPostcode: null
};

/**
 * Getters
 */
const getters = {
    /** Returns the current availability */
    availabilities: (state) => state.booking.availability,

    /** Retrieve the booking and generate if one doesn't exist */
    booking: (state) => state.booking,

    /** The valid postcode stored on the booking */
    bookingPostcode: (state) => state.booking.address.postcode,

    /** The valid postcode stored on the booking */
    bookingPostcodeHasBeenSet: (state) => (state.booking.address.postcode !== null && state.booking.address.postcode !== undefined),

    /** Gets the total in the cart */
    cartTotal: (state, getters) => {
        let nullNumber = 0;
        let toFixed = 2;

        if (getters.countryCodeSetting === 'DE') {
            toFixed = 0;
        }

        return (state.booking.cart.total_price) ? state.booking.cart.total_price.toFixed(toFixed) : nullNumber.toFixed(toFixed);
    },

    /** Returns all of the devices within the cart */
    devices: (state) => state.booking.cart.devices,

    /** Returns the total number of devices in the cart */
    devicesInCart: (state) => Object.keys(state.booking.cart.devices).length,

    /** The discount code in the cart */
    discountCodeInCart: (state) => state.booking.cart.discount_code,

    /** Gets the discount total in the cart */
    discountTotal: (state, getters) => {
        let nullNumber = 0;
        let toFixed = 2;

        if (getters.countryCodeSetting === 'DE') {
            toFixed = 0;
        }

        return (state.booking.cart.discount_code_amount) ? state.booking.cart.discount_code_amount.toFixed(toFixed) : nullNumber.toFixed(toFixed);
    },

    /** Gets the full price excluding the discount */
    fullPrice: (state, getters) => {
        let nullNumber = 0;
        let toFixed = 2;

        if (getters.countryCodeSetting === 'DE') {
            toFixed = 0;
        }

        return (state.booking.cart.full_price) ? state.booking.cart.full_price.toFixed(toFixed) : nullNumber.toFixed(toFixed);
    },

    /** Returns a bool of if there's availability or not */
    hasAvailability: (state) => state.booking.availability && Object.keys(state.booking.availability).length > 0,

    /** Returns a bool of if there's devices or not */
    hasDevices: (state) => Object.keys(state.booking.cart.devices).length > 0,

    /** The selected warranty status */
    inWarrantyStatus: (state) => state.booking.in_warranty,

    /** The warranty booking is chargeable */
    warrantyRepairChargeable: (state) => (state.booking.cart.warranty_repair_is_chargeable && (state.booking.cart.warranty_repair_charge && state.booking.cart.warranty_repair_charge > 0)),

    /** The warranty booking charge */
    warrantyRepairCharge: (state, getters) => {
        let nullNumber = 0;
        let toFixed = 2;

        if (getters.countryCodeSetting === 'DE') {
            toFixed = 0;
        }

        return (state.booking.cart.warranty_repair_charge) ? state.booking.cart.warranty_repair_charge.toFixed(toFixed) : nullNumber.toFixed(toFixed);
    },

    /** Check if a problem is in the sale */
    problemIsInTheSale: (state) => problemPricing => (!state.booking.in_warranty && state.booking.cart.sale_active && problemPricing.price_has_reduction),

    /** Get the purchased date of the in warranty device **/
    purchaseDate: (state) => state.booking.purchase_date,

    /** Booking reference */
    reference: (state) => state.reference,

    /** Get the purchased date of the in warranty device **/
    emailAddress: (state) => state.booking.email,

    /** Get the selected repair location option */
    repairLocationOption: (state) => state.repairLocationOption,

    /** Determine if the selected repair location option is In Store Drop Off */
    isStoreDropOff: (state) => state.repairLocationOption === 'drop-off',

    /** Returns the problems that have been restricted based on the warranty status */
    restrictedProblems: (state) => state.booking.restricted_problems,

    /** Returns a bool of if we're using a manual address or not */
    useManualAddress: (state) => state.useManualAddress,

    landingPageLoaded: (state) => state.landingPageLoaded,
    landingPageLoadSuccess: (state) => state.landingPageLoadSuccess,

    disableBasketButtonOnConfirmDamagePage:  (state) => state.disableBasketButtonOnConfirmDamagePage,

    wasBookedInStore: (state) => !!(state.booking.store_code),

    chosenDropOffStoreId: (state) => state.booking.drop_off_company_store_id,

    /** Returns a bool of if the booking has been persisted and set as a drop off booking */
    bookingIsDropOff: (state) => (state.booking.drop_off_company_store_id) ? state.booking.drop_off_company_store_id : false,

    bookingIsMailIn: (state) => state.booking.is_mail_in_repair,

    /** Local selection of the drop off store, note: this has not been persisted @see bookingIsDropOff */
    requestedDropOffStoreId: (state) => state.requestedDropOffStoreId,

    customerPostcode: (state) => state.customerPostcode,

    /** Analytics ecommerce data */
    analyticsEcommerceData: (state, getters) => {
        return {
            "transaction_id": getters.booking.order_id,
            "affiliation": `${getters.carrierDetails.formattedName} B2C Booking`,
            "value": getters.cartTotal,
            "currency": getters.carrierDetails.currency,
            'items': getters.analyticsEcommerceProducts
        };
    },

    /** Analytics ecommerce product data */
    analyticsEcommerceProducts: (state, getters) => {
        let products = [];

        for (let deviceId in getters.devices) {
            let device = getters.devices[deviceId].device;
            let problemsPricing = getters.devices[deviceId].problems_pricing;
            for (let problemId in problemsPricing) {
                let problem = problemsPricing[problemId].problem;
                products.push({
                    'id': problem.id,
                    'name': device.title + " " + problem.title,
                    'price': problemsPricing[problemId].price,
                    'quantity': 1
                });
            }
        }

        return products;
    }
};

/**
 * Actions
 */
const actions = {
    /** Add a promo code to the booking */
    addPromoCode({commit, getters}, code) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/discount-code`, {
                discount_code: code

            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve();

            }).catch(e => {
                reject(e);
            });
        });
    },

    /** Check we have the booking and dispatch the generator if not */
    checkBooking({dispatch, getters, state}) {
        let bookingRef = router.currentRoute.query.ref;

        if (!getters.landingPageLoadSuccess || (bookingRef && bookingRef.length > 0)) {

            if (router.currentRoute.name === 'partnerRepairHome') return;
            if(!getters.isInStoreBooking) {
                return router.push({name: 'partnerRepairHome', query: { ref: bookingRef ? bookingRef : 'unknown' }});
            }
        }

        if (!state.reference || !Object.keys(state.booking).length) {
            dispatch('generateBooking');
        }
    },

    /** Check we have the booking and dispatch the generator if not */
    checkBookingIsComplete({state}) {
        return new Promise((resolve) => {
            if (state.booking.checkout_status === 'SUCCESS') {
                router.push({name: 'bookingComplete'});
            }
            resolve();
        });
    },

    /** Clears the booking */
    clearBooking({commit, dispatch}) {
        return new Promise((resolve) => {
            commit('CLEAR_BOOKING');
            dispatch('clearDevice');
            dispatch('clearReferrer');
            dispatch('generateBooking');
            dispatch('setBasketButtonOnConfirmDamagePage', true);
            resolve();
        });
    },

    /** Clears the booking postcode ready for re-entry */
    clearBookingPostcode({commit}) {
        commit('CLEAR_BOOKING_POSTCODE');
    },

    /** Generate a new booking */
    generateBooking({commit}) {
        return new Promise((resolve) => {
            this.$axios.post('/api/v4/wefix-booking/build')
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve();
                });
        });
    },
    getBookingByReference({commit, dispatch, getters}, reference) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/wefix-booking/${reference}`, {bypassErrorCheck: true})
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);

                    if (!getters.hasDevices) {
                        dispatch('clearDevice');
                        commit('SET_LANDING_PAGE_LOADED', true);
                        commit('SET_LANDING_PAGE_LOADED_SUCCESS', true);
                        return resolve();
                    }

                    dispatch('retrieveDevice', {
                        deviceKey: Object.keys(getters.devices)[0],
                        setModelVersionId: true
                    }).then(() => {
                        commit('SET_LANDING_PAGE_LOADED', true);
                        commit('SET_LANDING_PAGE_LOADED_SUCCESS', true);
                        resolve();
                    });

                }).catch(() => {
                    // Fall back to the usual generate/build endpoint if this fails.
                    commit('CLEAR_BOOKING');
                    dispatch('clearDevice');
                    commit('SET_LANDING_PAGE_LOADED', true);
                    commit('SET_LANDING_PAGE_LOADED_SUCCESS', false);
                    reject();
                });
        });
    },

    /** Loads the availability for a doorstop collection */
    loadAvailability({commit, getters}, {startDate, daysToLoad}) {
        return new Promise((resolve, reject) => {
            if (!getters.bookingPostcode) {
                return resolve();
            }

            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/availability`, {
                start_date: startDate,
                days_to_load: daysToLoad
            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve();
            }).catch(e => {
                reject(e);
            });
        });
    },

    /** Remove the promo code from the booking */
    removePromoCode({commit, getters}) {
        return new Promise((resolve, reject) => {
            this.$axios.delete(`/api/v4/wefix-booking/${getters.reference}/discount-code`)
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve();

                }).catch(e => {
                reject(e);
            });
        });
    },

    /** Retrieve a refreshed instance of the booking */
    retrieveBooking({commit, getters}) {
        return new Promise((resolve) => {
            this.$axios.get(`/api/v4/wefix-booking/${getters.reference}`)
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve();
                });
        });
    },

    retrieveBookingByRef({commit}, ref) {
        return new Promise((resolve) => {
            this.$axios.get(`/api/v4/wefix-booking/by/ref/${ref}`).then(response => {
                  commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                  resolve();
              });
        });
    },

    /** Retrieve a refreshed instance of the booking */
    retrieveBookingByToken({commit, dispatch, getters}, token) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/wefix-booking/by/token/${token}`).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                this.$axios.post('/api/v4/wefix-booking/'+ getters.reference + '/validate', {}).then(response => {
                    // If this endpoint succeeds, we redirect to payment. Otherwise, the global error handling should
                    //   redirect the user to the correct screen.
                    dispatch('retrieveDevice', {
                        deviceKey:Object.keys(response.data.data.booking.cart.devices)[0],
                        setModelVersionId: true
                    }).then(() => {
                        resolve();
                    });
                });
            }).catch(e => {
                reject(e);
            });
        });
    },

    /** Set the address */
    setAddress({commit, dispatch}, address) {
        return new Promise((resolve, reject) => {
            commit('SET_BOOKING_ADDRESS', address);

            dispatch('setCustomerDetails', { address: address }).then(() => {
                resolve();
            }).catch(e => {
                reject(e);
            });
        });
    },

    /** Set the booking postcode */
    setBookingPostcode({commit, dispatch, getters}, {postcode, requestParams: {model_version_id = null, problems = []}}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/postcode`, {
                postcode: postcode,
                carrier: getters.carrier,
                is_business_account: getters.isBusinessAccount,
                repair_type: getters.repairType,
                model_version_id: model_version_id,
                problems: problems
            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                if (response.data.data.booking_device) {
                    dispatch('setDeviceData', response.data.data.booking_device);
                }
                resolve();
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    /** Set the booking address */
    setBookingAddress({commit, getters}, {line_1, line_2, town, postcode, latitude, longitude, requestParams: {model_version_id = null, problems = []}}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/address`, {
                line_1: line_1,
                line_2: line_2,
                town: town,
                postcode: postcode,
                latitude: latitude,
                longitude: longitude,
                carrier: getters.carrier,
                is_business_account: getters.isBusinessAccount,
                model_version_id: model_version_id,
                problems: problems
            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve();
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Store the customers postcode so we can update the booking postcode to the drop off location and still be able
     * to reset the booking back to their postcode if required.
     */
    setCustomerPostcodeCache({commit}, postcode) {
        return new Promise((resolve) => {
            commit('SET_CUSTOMER_POSTCODE', postcode);
            resolve();
        });
    },

    /**
     * Persists the company store id against the booking as the drop off location
     */
    setDropOffCompanyStoreId({commit, getters}, companyStoreId) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/drop-off`, {
                drop_off_company_store_id: companyStoreId
            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Persists the company store id against the booking as the drop off location
     */
    removeDropOffCompanyStoreId({commit, getters}) {
        return new Promise((resolve, reject) => {
            this.$axios.delete(`/api/v4/wefix-booking/${getters.reference}/drop-off`).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },

    /** Set the customer details and address */
    setCustomerDetails({commit, getters}, customerDetails) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/customer-details`, customerDetails)
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Set the customer details and address */
    updateKlarna({commit, getters}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/update-klarna`)
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Set the date for the booking */
    setDate({commit, getters}, date) {
        return new Promise((resolve, reject) => {
            commit('SET_BOOKING_DATE', date);

            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/date`, {
                date: date

            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve();
            })
            .catch(error => {
                commit('SET_BOOKING_DATE', null);
                reject(error);
            });
        });
    },

    /** Set the repair location option */
    setRepairLocationOption({commit}, location) {
        return new Promise((resolve) => {
            commit('SET_REPAIR_LOCATION_OPTION', location);
            resolve();
        });
    },

    /** Sets if we use a manual address or from the auto list */
    setManualAddressState({commit}, state) {
        return new Promise((resolve) => {
            commit('SET_USE_MANUAL_ADDRESS', state);
            resolve();
        });
    },

    /** Set the warranty date, purchased date, and imei of the warranty device */
    setWarrantyDetails({commit, dispatch, getters}, {purchaseDate: purchaseDate = null, imei: imei = null}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/warranty`, {
                in_warranty: getters.inWarrantyStatus,
                purchase_date: purchaseDate,
                imei: imei

            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);

                if (response.data.data.booking_device) {
                    dispatch('setDeviceData', response.data.data.booking_device);
                }

                resolve();

            }).catch(e => {
                reject(e);
            })
        });
    },

    /** Sets the warranty status */
    setWarrantyState({dispatch, commit, getters}, warrantyState) {
        return new Promise((resolve) => {
            if (!warrantyState && getters.inWarrantyStatus) {
                commit('CLEAR_PROBLEMS');
            }

            commit('SET_WARRANTY_STATE', warrantyState);

            if (!warrantyState) {
                dispatch('setWarrantyDetails', {purchaseDate: null, imei: null});
            }

            dispatch('loadProblems');
            resolve();
        });
    },

    /** Confirm the warranty booking */
    confirmWarrantyBooking({commit, getters}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/confirm-iw`, {})
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Confirm the promotional event booking */
    confirmPromoBooking({commit, getters}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/confirm-promo`, {})
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Charge a booking */
    chargeBooking({commit, getters}, {paidViaCredit, token, paymentType}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/charge`, {
                paid_via_credit: paidViaCredit,
                currency: process.env.VUE_APP_CURRENCY ? process.env.VUE_APP_CURRENCY : 'GBP',
                token: token,
                payment_type: paymentType,
                invoice_payment: (paymentType === "invoice_payment")
            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);
                resolve(response);
            }).catch(error => {
                // TODO: Change warranty state and refresh
                reject(error);
            });
        });
    },

    /** A confirmDamage option has been selected, show the sidebar basket button on the confirm damage page */
    setBasketButtonOnConfirmDamagePage({commit}, value) {
        return new Promise((resolve) => {
            commit('SET_DISABLE_BASKET_BUTTON_ON_CONFIRM_DAMAGE_PAGE', value);
            resolve();
        });
    },

    setRequestedDropOffStoreId({commit}, value) {
        return new Promise((resolve) => {
            commit('SET_REQUESTED_DROP_OFF_STORE_ID', value);
            resolve();
        });
    },
};

/**
 * Mutations
 */
const mutations = {
    /** Clear the warranty state */
    CLEAR_WARRANTY_STATE(state) {
        state.booking.in_warranty = null;
    },

    /** Clear the booking */
    CLEAR_BOOKING(state) {
        state.reference = null;
        state.booking = {
            address: {
                postcode: null
            },
            availability: {},
            billing_address: {},
            braintree_client_token: null,
            cart: {
                devices: {},
                discount_code: null,
                discount_code_amount: 0,
                discount_percentage: 0,
                full_price: 0,
                sale_active: false,
                sale_title: null,
                total_price: 0
            },
            checkout_status: null,
            date: null,
            email: null,
            email_consent: false,
            first_name: null,
            full_date: null,
            in_warranty: null,
            klarna_client_token: null,
            klarna_session_id: null,
            last_name: null,
            phone_number: null,
            purchase_date: null,
            restricted_problems: [],
            drop_off_company_store_id: null
        };
        state.repairLocationOption = 'doorstep';
        state.useManualAddress = false;
        state.requestedDropOffStoreId = null;
    },

    /** Clear the booking postcode */
    CLEAR_BOOKING_POSTCODE(state) {
        state.booking.address.postcode = null;
    },

    /** Sets the address for the booking */
    SET_BOOKING_ADDRESS(state, address) {
        state.booking.address = address;
    },

    /** Sets the date for the booking */
    SET_BOOKING_DATE(state, date) {
        state.booking.date = date;
    },

    /** Set the reference and booking data from a response */
    SET_BOOKING_FROM_RESPONSE(state, response) {
        state.reference = response.booking_reference;
        state.booking = response.booking;
    },

    /** Set the repair location option */
    SET_REPAIR_LOCATION_OPTION(state, location) {
        state.repairLocationOption = location;
    },

    /** Set if we're using a manual address or not */
    SET_USE_MANUAL_ADDRESS(state, manual) {
        state.useManualAddress = manual;
    },

    /** Set the warranty state */
    SET_WARRANTY_STATE(state, warrantyState) {
        state.booking.in_warranty = warrantyState;
    },

    /** Load landing page */
    SET_LANDING_PAGE_LOADED(state, loaded) {
        state.landingPageLoaded = loaded;
    },

    SET_LANDING_PAGE_LOADED_SUCCESS(state, success) {
        state.landingPageLoadSuccess = success;
    },

    SET_DISABLE_BASKET_BUTTON_ON_CONFIRM_DAMAGE_PAGE(state, value) {
        state.disableBasketButtonOnConfirmDamagePage = value;
    },

    SET_REQUESTED_DROP_OFF_STORE_ID(state, value) {
        state.requestedDropOffStoreId = value;
    },

    SET_CUSTOMER_POSTCODE(state, value) {
        state.customerPostcode = value;
    },

    SET_DROP_OFF_COMPANY_STORE_ID(state, value) {
        state.booking.drop_off_company_store_id = value;
    }
};

export default {
    state,
    getters,
    actions,
    mutations
}